/**
 * URL sanitizer for captured network request URLs.
 *
 * Two layers of defense:
 *
 * 1) Param NAME match — anything whose name suggests a credential is
 *    redacted unconditionally (token, key, password, secret, auth, etc.).
 *
 * 2) Param VALUE shape match — even when the name is innocent ("id",
 *    "redirect_uri", "context"), if the VALUE looks like a JWT, a
 *    Bearer token, an mhosaic-feedback API key, or a long opaque hex/b64
 *    string we redact it. This catches the realistic case where a backend
 *    routes credentials through generic-named params or where a careless
 *    developer puts a token in a URL fragment for "convenience."
 *
 * The path portion is preserved as-is — its purpose is operator-side
 * "which page was this report filed from", and stripping it would
 * destroy that signal. Hosts that route secrets through path segments
 * (uncommon) should mark those routes for the widget to skip via the
 * sanitizeUrl hook in createFeedback().
 */

const SENSITIVE_NAME = /token|key|password|secret|auth|session|sig|credential|bearer|cookie/i

const SENSITIVE_VALUE_PATTERNS: RegExp[] = [
  // JWT: three base64url segments separated by dots, header begins with eyJ.
  /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/,
  // Mhosaic feedback keys (the project's own format).
  /^(?:sk|pk)_proj_[A-Za-z0-9_-]{16,}$/,
  // GitHub PATs (ghp/gho/ghu/ghs/ghr prefixes, 36+ chars).
  /^gh[pousr]_[A-Za-z0-9]{30,}$/,
  // Stripe live/test secret keys.
  /^(?:sk|pk|rk|whsec)_(?:live|test)_[A-Za-z0-9]{16,}$/,
  // AWS access key id.
  /^AKIA[0-9A-Z]{12,}$/,
  // Slack bot tokens.
  /^xox[abprso]-[A-Za-z0-9-]{12,}$/,
  // Generic high-entropy: hex 40+ chars (SHA-1, SHA-256) or long alnum 32+.
  /^[a-f0-9]{40,}$/i,
  // Long opaque base64-ish string (common for OAuth codes & session ids).
  /^[A-Za-z0-9_-]{40,}$/,
]

function looksLikeCredential(value: string): boolean {
  return SENSITIVE_VALUE_PATTERNS.some((re) => re.test(value))
}

export function sanitizeUrl(url: string): string {
  try {
    // Accept absolute URLs or root-relative paths; reject everything else
    const isAbsolute = /^https?:\/\//i.test(url)
    const isRootRelative = url.startsWith('/')
    if (!isAbsolute && !isRootRelative) return url

    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const u = new URL(url, base)
    const clean = new URLSearchParams()
    u.searchParams.forEach((value, name) => {
      if (SENSITIVE_NAME.test(name) || looksLikeCredential(value)) {
        clean.set(name, '[redacted]')
      } else {
        clean.set(name, value)
      }
    })
    u.search = clean.toString()
    // Hash fragments occasionally carry credentials (OAuth implicit flow):
    // never log them.
    u.hash = u.hash ? '#[redacted]' : ''
    return u.toString()
  } catch {
    return url
  }
}
